"use client";

import type { ReactNode } from "react";
import { useState } from "react";
import { commitToSaveMode } from "../../lib/field-types/commit-save-mode";
import {
	EditableFieldBody,
	EditableFieldConfirmActions,
	EditableFieldFooter,
	EditableFieldHeader,
	EditableFieldPencilButton,
	EditableFieldRoot,
	EditableFieldTitle,
} from "../custom/editable-field";
import { TextareaField } from "./textarea-field";
import { TextField } from "./text-field";

export type EditableFieldCommitMode = "live" | "confirm" | "immediate";

export interface EditableFieldControlProps {
	title: string;
	commit?: EditableFieldCommitMode;
	className?: string;
	headerActions?: ReactNode;
	value: string;
	committedValue?: string;
	onChange: (value: string) => void;
	/** Fired only on confirm when commit=confirm — not on every keystroke. */
	onCommit?: (value: string) => void;
	onCancelEdit?: () => void;
	fieldId: string;
	fieldType?: "text" | "textarea";
	description?: string;
	error?: ReactNode;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	maxChars?: number;
	minRows?: number;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
}

/**
 * EditableField chrome + FieldRenderer atoms. Under `commit=confirm`, keystrokes
 * update local draft only; `onCommit` runs from ConfirmActions.
 */
export function EditableFieldControl({
	title,
	commit = "confirm",
	className,
	headerActions,
	value,
	committedValue,
	onChange,
	onCommit,
	onCancelEdit,
	fieldId,
	fieldType = "text",
	description,
	error,
	name,
	onBlur,
	disabled,
	maxChars,
	minRows,
	mixed,
	mixedTooltip,
	mixedPlaceholder,
}: EditableFieldControlProps) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value);
	const baseline = committedValue ?? value;
	const displayValue = commit === "confirm" && editing ? draft : value;
	const isDirty = draft !== baseline;

	function handleChange(next: string) {
		if (commit === "confirm") {
			setDraft(next);
			return;
		}
		onChange(next);
	}

	function handleConfirm() {
		onChange(draft);
		onCommit?.(draft);
		setEditing(false);
	}

	function handleCancel() {
		setDraft(baseline);
		onCancelEdit?.();
		setEditing(false);
	}

	return (
		<EditableFieldRoot editing={editing} className={className}>
			<EditableFieldHeader>
				<EditableFieldTitle>{title}</EditableFieldTitle>
				<div className="flex items-center gap-1">
					{headerActions}
					{!editing ? (
						<EditableFieldPencilButton
							ariaLabel={`Edit ${title}`}
							onClick={() => {
								setDraft(value);
								setEditing(true);
							}}
						/>
					) : null}
				</div>
			</EditableFieldHeader>
			<EditableFieldBody>
				{editing || commit !== "confirm" ? (
					fieldType === "textarea" ? (
						<TextareaField
							id={fieldId}
							label={title}
							description={description}
							error={error}
							name={name}
							onBlur={onBlur}
							disabled={disabled}
							value={displayValue}
							onChange={handleChange}
							maxChars={maxChars}
							minRows={minRows}
							mixed={mixed}
							mixedTooltip={mixedTooltip}
							mixedPlaceholder={mixedPlaceholder}
							saveMode={
								// Session footer owns confirm chrome; atom stays live/inline while editing.
								commit === "confirm"
									? commitToSaveMode("live")
									: commitToSaveMode(commit)
							}
						/>
					) : (
						<TextField
							id={fieldId}
							label={title}
							description={description}
							error={error}
							name={name}
							onBlur={onBlur}
							disabled={disabled}
							value={displayValue}
							onChange={handleChange}
							mixed={mixed}
							mixedTooltip={mixedTooltip}
							mixedPlaceholder={mixedPlaceholder}
						/>
					)
				) : (
					<p className="whitespace-pre-wrap text-sm text-foreground">
						{value.trim().length > 0 ? value : "—"}
					</p>
				)}
			</EditableFieldBody>
			{editing && commit === "confirm" ? (
				<EditableFieldFooter>
					<EditableFieldConfirmActions
						isDirty={isDirty}
						onConfirm={handleConfirm}
						onCancel={handleCancel}
						allowCancelWhenClean
					/>
				</EditableFieldFooter>
			) : null}
		</EditableFieldRoot>
	);
}
